/**
 * #285 — the card shown while a camera pin is hovered (was MarkerTooltip in MapView.tsx).
 *
 * One width, so every card and every picture is the same size. Blocks in order of importance:
 * name and status, the last event's picture, the last event, then where the camera is (typed
 * place, address, groups, Plus Code last). The picture box is 4:3 and reserved before the image
 * loads, and the thumbnail is shown whole (`object-contain`): the old card cut a 64 px strip out
 * of a 640x480 picture. Three text sizes: 15 px name, 12 px body, 11 px meta.
 *
 * The card is drawn at page level (a portal on document.body, fixed positioning), so the map's
 * box cannot clip it and its cluster bubbles cannot cover it: the dashboard map panel is shorter
 * than the card, so no in-map placement could ever fit there. Once drawn it measures itself and
 * the pin, goes below the pin when the room above (in the viewport) is short, slides sideways at
 * the viewport's edges with the arrow sliding the other way so it still points at the pin, and
 * is placed again when the page scrolls, the window resizes or the map pans or zooms.
 *
 * The pin itself keeps its image role: nothing of the card is inside the SVG (item 282 review).
 *
 * #288 — two faults seen on prod after 285:
 *  - the map takes a hovered pin's element off the page when the pin joins a group circle on zoom
 *    (and during the minute refresh); no mouseleave follows, and measured off the page the pin's
 *    box is all zeros, so the card jumped to the top left corner and stayed. A pin with no box now
 *    ends the hover (`onLost`); the next hover opens the card as usual.
 *  - the browser draws only the full-screen element and what is inside it, so a card on the body
 *    vanished when the map was expanded. While something is full screen the card is drawn inside
 *    it (it covers the whole screen, so the fixed placement against the viewport still holds).
 *
 * #289 — the one hover standard: the shared hover look and motion (index.css), opened by
 * useHoverIntent in CameraMarker. The card takes the pointer (WCAG 1.4.13 hoverable): entering it
 * keeps it open, leaving it starts the grace, and a click on it does what a click on the pin does.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react'
import type { RefObject } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useMap } from '@vis.gl/react-google-maps'
import { Compass } from 'lucide-react'
import { cn, formatCardDateTime } from '@/lib/utils'
import { PlusCode } from '@/components/maps/PlusCode'
import { CARD_GAP, placeHoverCard, type Placement } from './hoverCardPlacement'
import type { MapMarker } from './MapView'

interface Pos {
  placement: Placement
  shiftX: number
  /** Vertical slide when the card fits on neither side (short screens): the arrow is dropped. */
  shiftY: number
  /** The arrow's centre, px from the card's left edge. */
  arrowX: number
  /** Viewport x of the pin's centre: the card is centred here, then slid by shiftX. */
  left: number
  /** Viewport y of the card's top edge. */
  top: number
  /** False until the first measurement: the card is kept invisible so it never flashes at 0,0. */
  ready: boolean
}

// #288 — where the card is drawn: inside the full-screen element while there is one, else the page.
const subscribeFullscreen = (onChange: () => void) => {
  document.addEventListener('fullscreenchange', onChange)
  return () => document.removeEventListener('fullscreenchange', onChange)
}
const fullscreenElement = () => document.fullscreenElement ?? null

export function PinHoverCard({ marker, anchorRef, pinColour = '#fbbf24', onLost, instant = false, onEnter, onLeave, onActivate, onSetDirection, onThumbnailError, thumbnailUrl }: {
  marker: MapMarker
  anchorRef: RefObject<HTMLElement>
  /** #287 — the company's pin colour */
  pinColour?: string
  /** #288 — the pin has left the page (or has no size): the hover is over. */
  onLost?: () => void
  /** #289 — opened while warm: no enter motion */
  instant?: boolean
  /** #289 — the pointer entered / left the card */
  onEnter?: () => void
  onLeave?: () => void
  /** #289 — a click on the card */
  onActivate?: () => void
  /** #330 — superusers only: the card shows "Set direction", which calls this (and not onActivate). */
  onSetDirection?: () => void
  /** #336 — the picture failed (an expired link): the map's owner refreshes the pins once, so a fresh link arrives */
  onThumbnailError?: () => void
  /** #336 — the link to show, passed on its own by the pin (a refreshed link is written into the kept marker);
   *  the marker's own link when not given */
  thumbnailUrl?: string | null
}) {
  const { t } = useTranslation('cameras')
  const { t: tc } = useTranslation('common')
  const map = useMap()
  const ref = useRef<HTMLDivElement>(null)
  // #336 — the link that failed, not a flag: a fresh link (the pins refreshed) shows the picture again in an open card
  const [failedSrc, setFailedSrc] = useState<string | null>(null)
  const src = thumbnailUrl === undefined ? marker.thumbnailUrl ?? null : thumbnailUrl
  const imgFailed = !!src && failedSrc === src
  const [pos, setPos] = useState<Pos>({ placement: 'above', shiftX: 0, shiftY: 0, arrowX: 150, left: 0, top: 0, ready: false })
  const container = useSyncExternalStore(subscribeFullscreen, fullscreenElement) ?? document.body
  const onLostRef = useRef(onLost)
  onLostRef.current = onLost

  const measure = useCallback(() => {
    const anchor = anchorRef.current
    const card = ref.current
    if (!anchor || !card) return
    const pin = anchor.getBoundingClientRect()
    if (!anchor.isConnected || (pin.width === 0 && pin.height === 0)) {
      onLostRef.current?.()
      return
    }
    const size = card.getBoundingClientRect()
    const frame = { top: 0, left: 0, width: window.innerWidth, height: window.innerHeight }
    const { placement, shiftX, shiftY, arrowX } = placeHoverCard({ frame, pin, card: size })
    const next: Pos = {
      placement,
      shiftX,
      shiftY,
      arrowX,
      left: pin.left + pin.width / 2,
      top: (placement === 'above' ? pin.top - CARD_GAP - size.height : pin.bottom + CARD_GAP) + shiftY,
      ready: true,
    }
    setPos((prev) =>
      prev.ready && prev.placement === next.placement && prev.shiftX === next.shiftX && prev.shiftY === next.shiftY
        && prev.arrowX === next.arrowX && prev.left === next.left && prev.top === next.top
        ? prev
        : next,
    )
  }, [anchorRef])

  // Before the first paint, so the card never shows at the wrong place; and again whenever the
  // card's own size can change: the marker refreshes under an open card (every minute, and on
  // WebSocket events) and an expired picture drops the picture box; and when it moves into or out
  // of the full-screen map (#288).
  useLayoutEffect(measure, [measure, marker, imgFailed, container])

  // The pin moves under the pointer when the page scrolls, the window resizes or the map moves.
  useEffect(() => {
    window.addEventListener('scroll', measure, true)
    window.addEventListener('resize', measure)
    const moved = typeof map?.addListener === 'function' ? map.addListener('bounds_changed', measure) : null
    // #288 review — the group circles are made on idle, after the last bounds_changed of a zoom:
    // a pin that joined one is gone only then.
    const settled = typeof map?.addListener === 'function' ? map.addListener('idle', measure) : null
    return () => {
      window.removeEventListener('scroll', measure, true)
      window.removeEventListener('resize', measure)
      moved?.remove()
      settled?.remove()
    }
  }, [map, measure])

  const above = pos.placement === 'above'
  const shift = pos.shiftX
  // The card is centred on the pin (-50%) and then slid; the arrow sits back under the pin.
  const cardX = shift >= 0 ? `calc(-50% + ${shift}px)` : `calc(-50% - ${-shift}px)`
  const hasStatus = marker.isOnline === true || marker.isOnline === false
  const hasGroups = !!marker.groupNames && marker.groupNames.length > 0

  return createPortal(
    <div
      ref={ref}
      data-map-card
      data-placement={pos.placement}
      data-shift={shift}
      data-shift-y={pos.shiftY}
      data-instant={instant ? '' : undefined}
      className="hover-surface hover-motion z-[60] w-[300px] cursor-pointer p-3"
      onMouseEnter={onEnter}
      // Straight back onto its own pin (the card can overlap it on a short screen) is not a leave
      onMouseLeave={(e) => { if (!(e.relatedTarget instanceof Node && anchorRef.current?.contains(e.relatedTarget))) onLeave?.() }}
      // A click here must not also reach the marker through React (the card is portalled out of it)
      onClick={(e) => { e.stopPropagation(); onActivate?.() }}
      style={{ position: 'fixed', left: pos.left, top: pos.top, transform: `translateX(${cardX})`, visibility: pos.ready ? undefined : 'hidden' }}
    >
      {/* Header: name left, status right. The pill stays a div: tests and callers find it with closest('div'). */}
      <div className="flex items-start justify-between gap-2">
        <p className="m-0 min-w-0 break-words text-[15px] font-semibold leading-snug text-foreground">{marker.label}</p>
        {hasStatus && (
          <div
            className={cn(
              'inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase leading-snug tracking-wide',
              marker.isOnline
                ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300'   // #286 yellow = our camera, online
                : 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300',         // #286 red dot = offline
            )}
          >
            {/* #287 — an online camera's dot is the pin colour (outlined so white reads); offline stays red */}
            <span className={cn('h-1.5 w-1.5 rounded-full', marker.isOnline ? 'ring-1 ring-slate-800/70' : 'bg-red-500')} style={marker.isOnline ? { backgroundColor: pinColour } : undefined} />
            {marker.isOnline ? t('map.pin.online') : t('map.pin.offline')}
          </div>
        )}
      </div>

      {/* Picture: the whole last-event thumbnail in a 4:3 box reserved before it loads; an expired link hides it
          until the refreshed pins bring a fresh one (#336) */}
      {src && !imgFailed && (
        <div data-card-picture className="mt-2 aspect-[4/3] w-full overflow-hidden rounded-lg bg-muted">
          <img
            src={src}
            alt=""
            role="presentation"
            className="h-full w-full object-contain"
            onError={() => { setFailedSrc(src); onThumbnailError?.() }}
          />
        </div>
      )}

      {/* Last event: the type as the Dashboard names it, and a readable time */}
      {marker.lastEventType && (
        <p className="m-0 mt-2 flex items-center gap-1.5 text-xs leading-snug text-foreground/80">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{t('map.pin.last')}</span>
          <span className="min-w-0 truncate">
            {tc(`eventTypes.${marker.lastEventType}`, { defaultValue: marker.lastEventType.replace(/_/g, ' ') })}
          </span>
          {marker.lastEventAt && (
            <span className="ml-auto shrink-0 text-[11px] tabular-nums text-muted-foreground">{formatCardDateTime(marker.lastEventAt)}</span>
          )}
        </p>
      )}

      {/* Where: typed place, Google address, groups, and the Plus Code of the camera's true coordinates last */}
      <div className="mt-2 space-y-1 border-t border-border pt-2">
        {marker.sublabel && <p className="m-0 break-words text-xs leading-snug text-muted-foreground">{marker.sublabel}</p>}
        {marker.address && <p className="m-0 break-words text-[11px] leading-snug text-muted-foreground">{marker.address}</p>}
        {hasGroups && (
          <div className="flex flex-wrap gap-1">
            {marker.groupNames!.map((g, i) => (
              <span key={`${g}-${i}`} className="whitespace-nowrap rounded bg-muted px-1.5 py-0.5 text-[11px] font-medium leading-snug text-muted-foreground">
                {g}
              </span>
            ))}
          </div>
        )}
        <PlusCode lat={marker.lat} lng={marker.lng} copyable={false} className="text-[11px]" />
      </div>

      {/* #330 — the superuser's way into setting where this camera looks */}
      {onSetDirection && (
        <button
          type="button"
          className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-md border border-border px-2 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted"
          onClick={(e) => { e.stopPropagation(); onSetDirection() }}
        >
          <Compass aria-hidden className="h-3.5 w-3.5" />
          {t('map.direction.set')}
        </button>
      )}

      {/* Arrow towards the pin; it stays under the pin when the card has slid sideways, and is
          dropped when the card had to slide over the pin (it would point at nothing). */}
      {pos.shiftY === 0 && (
        <span
          data-card-arrow
          aria-hidden
          className={cn(
            'hover-arrow -translate-x-1/2',
            above ? '-bottom-1.5 border-b border-r' : '-top-1.5 border-l border-t',
          )}
          style={{ left: pos.arrowX }}
        />
      )}
    </div>,
    container,
  )
}
